import { computed, ref } from 'vue'
import { isValidEmail } from '../../utils/validation.js'

const normalize = (email) => String(email || '').trim().toLowerCase()

/**
 * « ADRESSE E-MAIL CONFIRMÉE AVEC LE CLIENT ».
 *
 * Retour Figma « TUNNEL 1er appel » : le commercial fait confirmer l'adresse
 * au client avant de pouvoir valider « Informations personnelles ». Le but est
 * d'éviter les coquilles — un prénom mal orthographié, un mauvais domaine, une
 * lettre qui manque, .fr pour .com — sur l'adresse qui recevra le devis.
 *
 * ON MÉMORISE L'ADRESSE CONFIRMÉE, et non un simple oui : la case vaut pour
 * CETTE adresse. Qu'elle change — corrigée ici, sur la fiche de la demande ou
 * depuis la fiche contact — et la case se décoche d'elle-même, sans aucune
 * surveillance à maintenir. Persistée dans `besoinData.emailConfirme`
 * (`{ email, at }`), que le questionnaire du client ne reçoit pas
 * (`pickBesoinCommun` ne retient que les champs communs).
 *
 * @param {Object} deps
 * @param {import('vue').Ref<Object|null>} deps.quote - Demande (null au chargement).
 * @param {Function} deps.getValue - Valeur d'un champ, édité ou d'origine.
 */
export function useBesoinEmailConfirm({ quote, getValue }) {
  const confirmed = ref(null)

  const currentEmail = computed(() => (quote.value ? String(getValue('email') || '').trim() : ''))

  const isEmailConfirmed = computed(() => (
    Boolean(confirmed.value) &&
    normalize(currentEmail.value) !== '' &&
    normalize(confirmed.value.email) === normalize(currentEmail.value)
  ))

  /**
   * Une adresse vide ou mal formée ne se fait pas « confirmer » : il n'y a rien
   * à relire avec le client, et le devis n'y arriverait pas.
   */
  const canConfirmEmail = computed(() => isValidEmail(currentEmail.value))

  /** Cocher enregistre l'adresse affichée à cet instant ; décocher l'oublie. */
  function setEmailConfirmed(value) {
    if (value && !canConfirmEmail.value) return
    confirmed.value = value ? { email: currentEmail.value, at: new Date().toISOString() } : null
  }

  /** Relit la confirmation enregistrée (clé `emailConfirme` de besoinData). */
  function restoreEmailConfirmed(saved) {
    confirmed.value = saved && typeof saved === 'object' && saved.email
      ? { email: saved.email, at: saved.at || null }
      : null
  }

  /** Ce qui s'enregistre : la confirmation si elle vaut encore, sinon rien. */
  const emailConfirmePayload = () => (isEmailConfirmed.value ? { ...confirmed.value } : null)

  /** La case est obligatoire pour confirmer « Informations personnelles ». */
  const emailMissing = computed(() => (
    !quote.value || isEmailConfirmed.value
      ? []
      : [{
          section: 'personal',
          field: 'email_confirme',
          label: 'Adresse e-mail confirmée avec le client',
          message: canConfirmEmail.value
            ? 'Cochez cette case pour pouvoir confirmer la section.'
            : 'L’adresse saisie n’est pas valide : corrigez-la, puis faites-la confirmer au client.'
        }]
  ))

  return {
    isEmailConfirmed, canConfirmEmail, setEmailConfirmed, restoreEmailConfirmed,
    emailConfirmePayload, emailMissing
  }
}
